<template>
  <b-card class="mb-05">
    <b-dropdown
      v-if="!isDiscarded"
      id="dropdown-1"
      toggle-class="my-custom-toggle"
      size="sm"
      right
      no-caret
      variant="outline-none"
      class="z-top"
    >
      <template #button-content>
        <feather-icon
          icon="MoreVerticalIcon"
          size="18"
          color="#000"
        />
      </template>
      <b-dropdown-item v-b-modal="`product-${product.id}`">
        <span>
          {{ $t('updatePrice') }}
        </span>
        <update-price :product="product" />
      </b-dropdown-item>
      <b-dropdown-item v-b-modal="`confirmDelete-${product.id}`">
        <span class="text-danger">
          Enviar a papelera
        </span>
      </b-dropdown-item>
    </b-dropdown>
    <b-row>
      <b-col class="d-flex">
        <div class="pr-1">
          <b-img
            width="90px"
            :src="`${product.product_catalog.logo}`"
          />
        </div>
        <div>
          <h4 class="mb-0 mt-1">
            {{ product.product_catalog.name }}
          </h4>
          <div class="d-flex flex-column">
            <span
              class="text-muted text-small mb-0"
            >{{ product.product_catalog.sku }} -
              <span class="font-weight-bolder text-dark">
                {{ product.product_catalog.brand || 'N/A' }}
              </span>
            </span>
            <div>
              <b-badge
                pill
                variant="primary"
                class="text-small"
              >
                {{ product.product_catalog.category.name }}
              </b-badge>
            </div>
            <div v-if="product.product_catalog.product_tags && product.product_catalog.product_tags.length">
              <b-badge
                v-for="tag in product.product_catalog.product_tags"
                :key="`tag-${tag.id}`"
                pill
                variant="light-info"
                class="text-small mb-25 mr-25"
              >
                {{ tag.name }}
              </b-badge>
            </div>
          </div>
        </div>
      </b-col>
      <b-col class="d-none d-md-block">
        <div class="px-2">
          <h5 class="mt-1 text-small">
            {{ $t('description') }}
          </h5>
          <div class="text-justify text-muted">
            {{ product.product_catalog.description }}
          </div>
        </div>
      </b-col>
      <b-col cols="auto">
        <div class="d-flex flex-column align-items-end px-1">
          <h5 class="mt-1 text-small">
            {{ $t('unitPrice') }}
          </h5>
          <div class="d-flex align-items-center">
            <h4 class="text-success mr-1">
              $ {{ formatStockablePrice(product.price_list) }}
            </h4>
            <h6>S/IVA</h6>
          </div>
          <div
            v-if="product.previous_price_list != null || product.last_price_updated_at"
            class="price-meta d-flex align-items-center flex-wrap justify-content-end mt-25"
          >
            <template v-if="product.previous_price_list != null">
              <span class="text-muted text-small">
                Antes:
                <span class="font-weight-bolder">
                  ${{ formatStockablePrice(product.previous_price_list) }}
                </span>
              </span>

              <b-badge
                v-if="priceDelta !== null"
                pill
                :variant="priceDelta >= 0 ? 'light-success' : 'light-danger'"
                class="ml-50 price-pill"
              >
                <span class="font-weight-bolder">
                  {{ priceDelta >= 0 ? '+' : '' }}${{ formatStockablePrice(Math.abs(priceDelta)) }}
                </span>

                <span
                  v-if="priceDeltaPct !== null"
                  class="ml-25"
                >
                  ({{ priceDeltaPct >= 0 ? '+' : '' }}{{ priceDeltaPct.toFixed(1) }}%)
                </span>

                <feather-icon
                  :icon="priceDelta >= 0 ? 'TrendingUpIcon' : 'TrendingDownIcon'"
                  size="14"
                  class="ml-25"
                />
              </b-badge>
            </template>

            <span
              v-if="product.last_price_updated_at"
              class="text-small text-muted ml-50 d-flex align-items-center"
            >
              <feather-icon
                icon="ClockIcon"
                size="14"
                class="mr-25"
              />
              {{ timeAgo(product.last_price_updated_at) }}
            </span>
          </div>
        </div>
      </b-col>
    </b-row>
    <b-row v-if="isDiscarded">
      <b-col class="d-flex justify-content-end">
        <b-button
          size="sm"
          variant="outline-primary"
          @click="restoreProduct(product.id)"
        >
          <feather-icon
            icon="RotateCcwIcon"
            size="14"
            class="mr-50"
          />
          Restaurar
        </b-button>
      </b-col>
    </b-row>

    <confirmation-modal
      :id="`confirmDelete-${product.id}`"
      :variant="'danger'"
      @confirm="deleteProduct(product.id)"
    >
      <template v-slot:content>
        Este producto se marcará como no manejado por la sucursal y se moverá a papelera.
      </template>
    </confirmation-modal>
  </b-card>
</template>

<script>
import { BCard } from 'bootstrap-vue'
import { mapGetters, mapActions } from 'vuex'
import UpdatePrice from '@/@core/components/UpdatePriceModal.vue'
import ConfirmationModal from '../ConfirmationModal.vue'
import { formatCurrencyNumber } from '@/utils/currency'

export default {
  components: {
    BCard,
    UpdatePrice,
    ConfirmationModal,
  },
  props: {
    product: {
      type: Object,
      required: true,
      default() {
        return {}
      },
    },
    isDiscarded: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('auth', ['currentUser']),
    priceDelta() {
      if (this.product?.previous_price_list == null || this.product?.price_list == null) return null
      const current = Number(this.product.price_list)
      const prev = Number(this.product.previous_price_list)
      if (Number.isNaN(current) || Number.isNaN(prev)) return null
      const d = current - prev
      return d === 0 ? null : d
    },
    priceDeltaPct() {
      if (this.priceDelta == null) return null
      const prev = Number(this.product.previous_price_list)
      if (!prev || Number.isNaN(prev)) return null
      return (this.priceDelta / prev) * 100
    },
  },
  methods: {
    ...mapActions('products', [
      'deleteStockableProduct',
      'restoreDiscardedStockableProduct',
    ]),

    restoreProduct(stockableId) {
      this.restoreDiscardedStockableProduct(stockableId).then(() => {
        this.$emit('product-updated')
      })
    },

    deleteProduct(stockableId) {
      this.deleteStockableProduct(stockableId).then(() => {
        this.$emit('product-updated')
      })
    },
    formatStockablePrice(value) {
      return formatCurrencyNumber(value, { precision: 6 })
    },
    timeAgo(isoDate) {
      if (!isoDate) return ''
      const date = new Date(isoDate)
      if (Number.isNaN(date.getTime())) return ''

      const diffMs = Date.now() - date.getTime()
      const diffMin = Math.floor(diffMs / 60000)
      const diffHrs = Math.floor(diffMin / 60)
      const diffDays = Math.floor(diffHrs / 24)

      if (diffMin < 1) return 'Actualizado hace unos segundos'
      if (diffMin < 60) return `Actualizado hace ${diffMin} min`
      if (diffHrs < 24) return `Actualizado hace ${diffHrs} h`
      if (diffDays < 7) return `Actualizado hace ${diffDays} día${diffDays === 1 ? '' : 's'}`

      return `Actualizado el ${date.toLocaleDateString()}`
    },
  },
}
</script>

<style lang="scss" scoped>
.bg-best-price {
  background-color: #77c77b;
}
.mb-05 {
  margin-bottom: 0.5rem;
}
.text-small {
  font-size: 10px;
}
.hover-candy:hover {
  transform: translateY(-5px);
}
#dropdown-1 {
  position: absolute;
  top: 0;
  right: 0;
}
.price-meta {
  gap: 0.5rem;
  opacity: 0.92;
}
.price-pill {
  font-size: 0.72rem;
  padding: 0.22rem 0.5rem;
}
</style>
